import { useEffect, useMemo, useState } from 'react';
import { buildEmbedParams } from '../../modules/GenomeWorkspaceView/CenterView/components/helpers/buildEmbedParams';
import { useUserData } from '../../modules/GenomeWorkspaceView/LeftView/hooks/useUserData';
import { useLookerDashboardContext } from '../../providers/LookerDashboardProvider';
import type { Filter } from '../../stores/filters';
import { useDashboardLookML, useImportDashboardFromLookML } from '../api/looker';
import { useCurrentWorkspace } from '../useCurrentWorkspace';
import type { FeatureFlag } from '../useFeatureFlag';
import { useActiveFeatureFlags } from '../useFeatureFlag/useFeatureFlag';
import { useFilters } from '../useFilters';
import { usePermissions } from '../usePermissions';
import { getDashboardBaseSections, getDashboardProtectedSections } from './dashboardSections';

interface IDashboardOptions {
  slug?: string;
  defaultFilters?: Filter[];
}

const EMPTY_DEFAULT_FILTERS: Filter[] = [];

export const useDashboard = ({ slug, defaultFilters = EMPTY_DEFAULT_FILTERS }: IDashboardOptions) => {
  const activeFeatureFlags = useActiveFeatureFlags();
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);
  const [connectionError, setConnectionError] = useState<Error>();

  const { userData } = useUserData('personalFolderId');
  const personalFolderId = userData.personalFolderId;
  const { hasPermission } = usePermissions();
  const { isUpdating, setIsUpdating, setConnection, catchCrossFilters } = useLookerDashboardContext();
  const { currentWorkspace, setLookerDashboardID } = useCurrentWorkspace();
  const { filters } = useFilters();

  const dashboardSections = useMemo(() => {
    if (!currentWorkspace?.workspace_type_id) return;

    const baseObjects = getDashboardBaseSections(currentWorkspace.workspace_type_id);
    const protectedObjects = getDashboardProtectedSections(currentWorkspace.workspace_type_id);

    return baseObjects.concat(
      Object.entries(protectedObjects).reduce((acc, [key, value]) => {
        if (
          Array.isArray(value)
            ? !value.every((permissionSubject) => hasPermission(permissionSubject))
            : !value(hasPermission, (flag: FeatureFlag) => activeFeatureFlags.includes(flag))
        ) {
          return acc;
        }

        acc.push(key);

        return acc;
      }, [] as string[]),
    );
  }, [currentWorkspace?.workspace_type_id, hasPermission]);

  const {
    data,
    error: dashboardLookMLError,
    isLoading: isDashboardLookMLLoading,
  } = useDashboardLookML({
    objectList: dashboardSections,
    // To mitigate the issue of hitting the ConnectionPool error
    source: 'gcs',
    slug,
  });

  const {
    data: dashboardID,
    isLoading: isImportDashboardLoading,
    error: dashboardError,
    reloadKey,
  } = useImportDashboardFromLookML({
    folderId: personalFolderId ?? '',
    lookml: data?.lookml,
    objectsHash: data?.objectsHash,
  });

  const isLoading = isDashboardLookMLLoading || isImportDashboardLoading || isDashboardLoading || isUpdating;

  const errorMessage = connectionError?.message || dashboardLookMLError?.message || dashboardError?.message;

  const filtersList = useMemo(() => {
    const filterValues = filters ? Object.values(filters) : [];
    const fallbackFilters = defaultFilters.filter((defaultFilter) => !filters?.[defaultFilter.filterName]?.expression);

    return [...filterValues, ...fallbackFilters];
  }, [filters, defaultFilters]);

  const dashboardParams = useMemo(() => buildEmbedParams(filtersList ?? []), [filtersList]);

  const handleDashboardRunStart = () => {
    setIsDashboardLoading(true);
  };

  const handleDashboardRunComplete = () => {
    setIsDashboardLoading(false);
    setIsUpdating(false);
  };

  useEffect(() => {
    setLookerDashboardID(dashboardID ?? '');
  }, [dashboardID, setLookerDashboardID]);

  return {
    isLoading,
    dashboardID,
    errorMessage,
    dashboardParams,
    reloadKey,
    handleDashboardRunStart,
    handleDashboardRunComplete,
    catchCrossFilters,
    setConnection,
    setConnectionError,
  };
};
